import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import { Site } from '../../../../common/types';

type ForcedStoreSelectionProps = {
  sites: Site[];
  onSiteSelected: (site: Site) => void;
  title?: string;
  subtitle?: string;
};

export default function ForcedStoreSelection({
  sites,
  onSiteSelected,
  title = 'Welcome to TPGE!',
  subtitle = 'Please select a store to view the menu and start your order.',
}: ForcedStoreSelectionProps) {
  const theme = useTheme();

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      onClose={() => { }} // Forced selection, no close
      PaperProps={{
        sx: {
          maxHeight: 'calc(100dvh - 48px)',
          borderRadius: 2,
        },
      }}
    >
      <Stack
        sx={{
          p: { xs: 2, sm: 2.5 },
          minHeight: 0,
          overflow: 'hidden',
        }}
        spacing={1.5}
      >
        <Stack spacing={0.75}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {subtitle}
          </Typography>
        </Stack>
        <Divider />

        <DialogContent
          sx={{
            p: 0,
            minHeight: 0,
            maxHeight: 'calc(100dvh - 220px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
          }}
        >
          <List sx={{ p: 0 }}>
            {sites.map((site) => (
              <ListItem
                key={site.id}
                disablePadding
                sx={{
                  mb: 1,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  '&:last-child': { mb: 0 },
                }}
              >
                <ListItemButton
                  onClick={() => onSiteSelected(site)}
                  sx={{ p: 1.25, borderRadius: 1, minHeight: 68 }}
                >
                  <ListItemAvatar sx={{ minWidth: 52 }}>
                    <Avatar sx={{ bgcolor: 'action.hover' }}>
                      <LocationOnOutlined sx={{ color: theme.palette.common.utilityOrange }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={site.name}
                    primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 700, noWrap: true }}
                    secondary={site.state || 'Available for pickup'}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>

        {sites.length === 0 && (
          <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading stores...</Typography>
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
